/**
 * @relatedFiles ../app/layout.tsx, ./landing/LandingPage.tsx, ./clerk-theme-provider.tsx
 * @purpose Manages authentication-based layout routing between landing page and authenticated app content
 * @notes Adds splash screen body class for unauthenticated users and handles public page routing
 */
"use client";

import { SignedIn, SignedOut, useUser } from '@clerk/nextjs';
import { usePathname } from "next/navigation";
import { useEffect } from "react";

import { LandingPage } from "@/components/landing/LandingPage";

import type { ReactNode } from 'react';

interface ClientLayoutProps {
  children: ReactNode;
}

export function ClientLayout({ children }: ClientLayoutProps) {
  const pathname = usePathname();
  const isPublicPage = pathname === '/privacy' || pathname === '/tos';
  const { isSignedIn } = useUser();

  useEffect(() => {
    if (!isSignedIn && !isPublicPage) {
      document.body.classList.add("has-splash-screen");
    } else {
      document.body.classList.remove("has-splash-screen");
    }
  }, [isSignedIn, isPublicPage]);

  return (
    <>
      <SignedOut>
        {isPublicPage ? (
          <div className="bg-white dark:bg-[hsl(var(--color-dark-bg-base))]">
            {children}
          </div>
        ) : (
          <LandingPage />
        )}
      </SignedOut>
      <SignedIn>
        {children}
      </SignedIn>
    </>
  );
} 